import { useCallback, useMemo, useState } from 'react';
import { useGetCatalogQuery } from '../../../redux/api/fymAPI';
import { CatalogFilterValue } from '../../../redux/api/fymAPI.types';
import { useDebouncedValue } from './useDebouncedValue';
import { useRealtimePollInterval } from '../../realtime/useRealtimePollInterval';

const SEARCH_DEBOUNCE_MS = 400;
/**
 * While a master is read, the table asks again every 10 s, so the Song cell turns ready on its own.
 * With the realtime socket connected, `catalog.song.updated` does that and the table does not poll.
 */
const PROCESSING_POLL_MS = 10000;

interface UseCatalogListProps {
  accountId: number;
}

/**
 * The catalog table's data and the two controls that narrow it.
 *
 * Both narrow it on the server: `no-link` is derived from a join the client
 * cannot repeat, and search has to match rows the current page never loaded.
 */
export function useCatalogList({ accountId }: UseCatalogListProps) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<CatalogFilterValue>(CatalogFilterValue.all);
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const options = {
    accountId,
    search: debouncedSearch === '' ? undefined : debouncedSearch,
    filter: filter === CatalogFilterValue.all ? undefined : filter,
  };
  const { data, isLoading, isFetching, error, refetch } = useGetCatalogQuery(options);
  const isProcessing = useMemo(
    () => data?.items.some((item) => item.song.masterStatus === 'processing') ?? false,
    [data]
  );
  // A second subscription that polls only while a master is read.
  const pollingInterval = useRealtimePollInterval(PROCESSING_POLL_MS);
  useGetCatalogQuery(options, { skip: !isProcessing, pollingInterval });

  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  return {
    items: data?.items,
    isLoading,
    isFetching,
    error,
    retry,
    search,
    setSearch,
    filter,
    setFilter,
  };
}
